
<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import { useApi } from '@/composables/useApi'
import holidayService from '@/services/holiday.service'
import { useAbility } from '@casl/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction' // For clicking events
import FullCalendar from '@fullcalendar/vue3'
import { computed, ref } from 'vue'
import { VSkeletonLoader } from 'vuetify/lib/labs/components.mjs'

definePage({
  meta: {
    name: 'holiday-calender',
    subject: 'global.menu',
    action: 'holiday',
  },
})

const { can } = useAbility()
const holidayEvents = ref([])

const dialog = ref(false)
const holidayName = ref('')
const selectedDate = ref(null)
const selectedEvent = ref(null)
const isEditing = ref(false)

const unitId = ref(cookieRef('selectedUnit').value ?? null)
const forceKey = ref(0)

const { 
  execute: fetchHolidays,
  isFetching: holidayLoading,
} = useApi(createUrl('/masters/holidays', {
  query: {
    unit_id: unitId,
  },
}), {
  afterFetch: ({ data }) => {
    holidayEvents.value = data.map(holiday => ({
      id: holiday.id,
      title: holiday.name,
      date: holiday.date,
    }))
    calendarOptions.value.events = holidayEvents.value
    forceKey.value++
  },
})

const canCreate = computed(() => can('create', 'master.holiday'))
const canUpdate = computed(() => can('update', 'master.holiday'))
const canDelete = computed(() => can('delete', 'master.holiday'))

const calendarOptions = ref({
  plugins: [dayGridPlugin, interactionPlugin],
  initialView: 'dayGridMonth',
  selectable: true,
  events: holidayEvents.value, 
  headerToolbar: {
    left: 'prev,next',   // no "today" here
    center: 'title',
    right: '',            // keep empty unless you want extra buttons
  },

  dateClick(info) {
    if (canCreate.value) {
      selectedDate.value = info.dateStr
      holidayName.value = ''
      isEditing.value = false
      dialog.value = true
    }
  },
  eventContent(arg) {
    const title = arg.event.title
    const el = document.createElement('div')

    el.innerHTML = `
      <div class="holiday-item">
      <span class="holiday-label">${truncateText(title, 15)}</span>
      ${canDelete.value ? `<span class="holiday-remove" title="Remove Holiday">&times;</span>` : ''}
      </div>
    `

    if (canUpdate.value) {
      el.querySelector('.holiday-item').addEventListener('click', () => {
        selectedEvent.value = arg.event
        holidayName.value = title
        isEditing.value = true
        dialog.value = true
      })
    }

    if (canDelete.value) {
      el.querySelector('.holiday-remove').addEventListener('click', async e => {
        e.stopPropagation()
        arg.event.remove()
        await holidayService.deleteHoliday(arg.event.id)
        await fetchHolidays()
      })
    }

    return { domNodes: [el] }
  },
  
})

const localloading = ref(false)

const saveHoliday = async () => {
  localloading.value = true
  if (isEditing.value && selectedEvent.value) {
    selectedEvent.value.setProp('title', holidayName.value)

    const idx = holidayEvents.value.findIndex(ev => ev.date === selectedEvent.value.startStr)
    if (idx > -1) {
      await holidayService.updateHoliday(selectedEvent.value.id, { name: holidayName.value, date: selectedEvent.value.startStr, unit_id: unitId.value }, async () => {
        localloading.value = false
        dialog.value = false
        await fetchHolidays()
      }, () => {
        localloading.value = false
      })
    }
  } else {
    const form = { name: holidayName.value, date: selectedDate.value, unit_id: unitId.value }

    await holidayService.addHoliday(form, async () => {
      localloading.value = false
      dialog.value = false
      await fetchHolidays()
    }, () => {
      localloading.value = false
    })
  }
}
</script>

<template>
  <VCard>
    <VCardText>
      <VRow>
        <VCol
          cols="12"
          class="px-2"
        >
          <VSkeletonLoader
            v-if="holidayLoading"
            type="card"
          />
          <FullCalendar
            v-else
            :key="forceKey"
            :options="calendarOptions"
            class="border rounded"
          />
          <VDialog
            v-model="dialog"
            max-width="400"
          >
            <VCard>
              <VCardTitle>Add Holiday</VCardTitle>
              <VCardText>
                <VTextField
                  v-model="holidayName"
                  label="Holiday Name"
                  autofocus
                />
              </VCardText>
              <VCardActions>
                <VSpacer />
                <VBtn
                  text
                  @click="dialog = false"
                >
                  Cancel
                </VBtn>
                <VBtn
                  color="primary"
                  :loading="localloading"
                  @click="saveHoliday"
                >
                  {{ isEditing ? 'Update' : 'Add' }}
                </VBtn>
              </VCardActions>
            </VCard>
          </VDialog>
        </VCol>
      </VRow>
    </VCardText>
  </VCard>
</template>

<style lang="scss">
@use "@core/scss/template/libs/full-calendar";

.holiday-item{
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.holiday-remove {
  color: white;
  cursor: pointer;
  font-size: 22px;
  margin-inline-start: 6px;
}

.holiday-label {
  color: white;
  cursor: pointer;
  font-size: 14px;
}

.fc-event {
  cursor: pointer;
  font-weight: bold;
}

.fc .fc-toolbar{
  justify-content: flex-end !important;
}


.calendars-checkbox {
  .v-label {
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
    opacity: var(--v-high-emphasis-opacity);
  }
}

.calendar-add-event-drawer {
  &.v-navigation-drawer:not(.v-navigation-drawer--temporary) {
    border-end-start-radius: 0.375rem;
    border-start-start-radius: 0.375rem;
  }
}

.calendar-date-picker {
  display: none;

  +.flatpickr-input {
    +.flatpickr-calendar.inline {
      border: none;
      box-shadow: none;

      .flatpickr-months {
        border-block-end: none;
      }
    }
  }

  & ~ .flatpickr-calendar .flatpickr-weekdays {
    margin-block: 0 4px;
  }
}

@media screen and (max-width: 1279px) {
  .calendar-add-event-drawer {
    border-width: 0;
  }
}
</style>
